import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useState } from 'react';
import { FilterCategory } from 'models/enums';
import Select, { Value } from 'domains/core/components/Select';
import { Send } from 'domains/campaigns/types';
import {
    ChannelSegmentFilter,
    RecordTypeFilter,
    SegmentFilter,
    SegmentFilterCategorySpecificField,
    SegmentFilterEngagementFields,
    SegmentFilterEventFields,
    SegmentFilterState,
} from 'domains/segments/types';
import ActivityFilter from './ActivityFilter';
import DemographicFilter, { DemographicFilterComponents } from './DemographicFilter';
import EventFilter, { EventFilterComponents } from './EventFilter';
import DeleteFilter from './DeleteFilter';
import IncludeNullValuesCheckbox from 'domains/segments/pages/SegmentCreatePage/Filter/IncludeNullValuesCheckbox';

// Define ActivityFilterComponents type locally
type ActivityFilterComponents = {
    channelSelect: React.ReactNode | null;
    operatorSelect: React.ReactNode | null;
    sendAutocomplete: React.ReactNode | null;
    valueAutocomplete: React.ReactNode | null;
};

const filterCategories: Value[] = Object.values(FilterCategory).map((category) => ({
    label: category,
    value: category,
}));
const matchTypes = ['AND', 'OR'];

export type Props = {
    eventRecordTypeFilters: RecordTypeFilter[];
    demographicFilters: SegmentFilter[];
    engagementFilters: SegmentFilter[] | ChannelSegmentFilter[];
    filter: SegmentFilterState<SegmentFilterCategorySpecificField>;
    hideMatchType: boolean;
    index: number;
    sends: Send[];
    value: any;
    handleChange: (e: any) => void;
    handleDatetimeError: (filterId: string, error: boolean) => void;
    removeFilter: (index: number) => void;
    updateFilter: (
        index: number,
        filter: SegmentFilterState<SegmentFilterCategorySpecificField>,
        resetPreview?: boolean
    ) => void;
};

const Filter = ({
    eventRecordTypeFilters,
    demographicFilters,
    engagementFilters,
    filter,
    hideMatchType,
    index,
    sends,
    value,
    handleChange,
    handleDatetimeError,
    removeFilter,
    updateFilter,
}: Props) => {
    const isCategoryActivity = Boolean(filter.category === FilterCategory.CONTACT_ACTIVITY);
    const isCategoryDetails = Boolean(filter.category === FilterCategory.CONTACT_DETAILS);
    const isCategoryEvent = Boolean(filter.category === FilterCategory.CONTACT_EVENTS);

    const [isFilterHighlighted, setIsFilterHighlighted] = useState(false);
    const [isOperatorValueHighlighted, setIsOperatorValueHighlighted] = useState(false);

    const updateSegmentFilter = (
        index: number,
        filter: SegmentFilterState<SegmentFilterCategorySpecificField>,
        resetPreview?: boolean
    ) => updateFilter(index, filter, resetPreview);

    // Get DemographicFilter components if category is CONTACT_DETAILS
    let demographicComponents: DemographicFilterComponents | null = null;
    if (isCategoryDetails) {
        demographicComponents = DemographicFilter({
            filter,
            filterOptions: demographicFilters,
            index,
            handleDatetimeError,
            updateSegmentFilter,
        });
    }

    // Get ActivityFilter components if category is CONTACT_ACTIVITY
    let activityComponents: ActivityFilterComponents | null = null;
    if (isCategoryActivity) {
        activityComponents = ActivityFilter({
            filter: filter as SegmentFilterState<SegmentFilterEngagementFields>,
            index,
            engagementFilters,
            sends,
            updateSegmentFilter,
        });
    }

    // Get EventFilter components if category is CONTACT_EVENTS
    let eventComponents: EventFilterComponents | null = null;
    if (isCategoryEvent) {
        eventComponents = EventFilter({
            eventRecordTypeFilters,
            filter: filter as SegmentFilterState<SegmentFilterEventFields>,
            index,
            isOperatorValueHighlighted,
            handleDatetimeError,
            updateSegmentFilter,
        });
    }

    return (
        <Stack>
            <Stack
                direction="column"
                spacing={2}
                sx={{
                    ...(isFilterHighlighted && {
                        backgroundColor: 'action.hover',
                    }),
                }}
            >
                {/* First Row: Category + IncludeNullValues + Filter Select + ActivityFilter Channel + EventFilter Operator */}
                <Stack direction="row" spacing={2} key={filter.id}>
                    {/* Category Select */}
                    <Stack>
                        <Select
                            data-test="category-select"
                            data-testid="category-select"
                            label="Filter type"
                            sx={{ textTransform: 'capitalize', width: '12rem', marginBottom: '0.2rem' }}
                            value={filter.category ?? ''}
                            values={filterCategories}
                            onChange={(e: any) => {
                                updateSegmentFilter(index, {
                                    ...filter,
                                    category: e.target.value,
                                    categorySpecificFields: {
                                        value: '',
                                    },
                                    name: '',
                                    operator: null,
                                    autocompleteValue: null,
                                    autocompleteInputValue: '',
                                });
                            }}
                        />
                        {isCategoryDetails && (
                            <IncludeNullValuesCheckbox
                                isNullValuesIncluded={filter.includeNullValues}
                                updateFilter={() => {
                                    updateSegmentFilter(index, {
                                        ...filter,
                                        includeNullValues: !filter.includeNullValues,
                                    });
                                }}
                            />
                        )}
                    </Stack>

                    {/* Filter Select (first component from DemographicFilter) */}
                    {isCategoryDetails && demographicComponents?.filterSelect}

                    {/* Channel Select (first component from ActivityFilter) */}
                    {isCategoryActivity && activityComponents?.channelSelect}

                    {/* Operator Select (first component from ActivityFilter if no channel enhancement) */}
                    {isCategoryActivity && !activityComponents?.channelSelect && activityComponents?.operatorSelect}

                    {/* Event Filter Operator (first component from EventFilter) */}
                    {isCategoryEvent && eventComponents?.operatorSelect}

                    {/* Delete Filter */}
                    <DeleteFilter
                        filter={filter}
                        index={index}
                        isCategoryEvent={isCategoryEvent}
                        removeFilter={removeFilter}
                        setIsFilterHighlighted={setIsFilterHighlighted}
                        setIsOperatorValueHighlighted={setIsOperatorValueHighlighted}
                        updateSegmentFilter={updateSegmentFilter}
                    />
                </Stack>

                {/* Second Row: Operator and Value for DemographicFilter OR ActivityFilter components */}
                {isCategoryDetails && (demographicComponents?.operatorSelect || demographicComponents?.valueInput) && (
                    <Stack direction="row" spacing={2}>
                        {demographicComponents?.operatorSelect}
                        {demographicComponents?.valueInput}
                    </Stack>
                )}

                {/* Second Row: ActivityFilter components (Operator, Send Autocomplete, Value Autocomplete) */}
                {isCategoryActivity &&
                    (activityComponents?.operatorSelect ||
                        activityComponents?.sendAutocomplete ||
                        activityComponents?.valueAutocomplete) && (
                        <Stack direction="row" spacing={2}>
                            {/* Operator Select (shown if channel enhancement is enabled) */}
                            {activityComponents?.channelSelect && activityComponents?.operatorSelect}
                            {/* Send Autocomplete */}
                            {activityComponents?.sendAutocomplete}
                            {/* Value Autocomplete */}
                            {activityComponents?.valueAutocomplete}
                        </Stack>
                    )}

                {/* Second Row: EventFilter components (Record Type, SubFilters, Date Operator, Date Values) */}
                {isCategoryEvent &&
                    (eventComponents?.recordTypeAndSubFilters ||
                        eventComponents?.dateOperatorSelect ||
                        eventComponents?.dateValueInput) && (
                        <Stack direction="row" spacing={2}>
                            {eventComponents?.recordTypeAndSubFilters}
                            {eventComponents?.dateOperatorSelect}
                            {eventComponents?.dateValueInput}
                        </Stack>
                    )}
            </Stack>

            {!hideMatchType && (
                <ToggleButtonGroup
                    aria-label="match type"
                    exclusive
                    onChange={handleChange}
                    style={{ margin: '16px' }}
                    value={value}
                >
                    {matchTypes.map((type) => (
                        <ToggleButton
                            aria-label={type}
                            key={type}
                            name="matchType"
                            sx={{ textTransform: 'lowercase' }}
                            value={type}
                        >
                            {type}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            )}
        </Stack>
    );
};

export default Filter;
